import {
  Component, OnInit,  ViewChild, ElementRef, ChangeDetectorRef,
  Inject,
} from '@angular/core';
import { DatePipe, DOCUMENT } from '@angular/common';
import {
  UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, AbstractControl,
  ValidatorFn,
} from '@angular/forms';
import * as _ from 'lodash';
import { CustomValidators } from 'shared/validation/custom-validators';

// data, models, helpers
import { segmentAnimation } from '../base/animations';
import CurrencyCodeMapper from 'utils/currency-code-mapper';
import { pricingSegmentData } from '../../segment-data/pricing-segment-data';
import { SegmentFieldItem } from 'utils/segment-field-item.interface';

// components
import { BaseSegmentComponent } from '../base/_base-segment.component';
import { IconsDropdownComponent } from 'shared/dropdown/icons-dropdown/icons-dropdown.component';
import { DetailsControlComponent } from 'shared/details-page/details-control/details-control.component';

// services
import { PriceSheetService } from 'shared/price-sheet/price-sheet.service';
import { BrandService } from 'shared/brand/brand.service';
import { ScorecardConnectorService } from 'shared/scorecard/scorecard-connector.service';
import { PricingScorecardService } from '../segments-scorecard/pricing-scorecard/pricing-scorecard.service';


@Component({
  moduleId: module.id,
  selector: 'pricing-segment',
  templateUrl: 'pricing.component.html',
  styleUrls: ['../base/_base-segment.component.scss', 'pricing.component.scss'],
  animations: [segmentAnimation],
  providers: [DatePipe],
})
export class PricingComponent extends BaseSegmentComponent implements OnInit {

  declare segmentForm: UntypedFormGroup;
  segmentData: any = pricingSegmentData;
  segmentName: string = 'prices'; // used in save request
  pathToScores: string = 'segments.prices.tallies';
  sortDirection: string = 'asc';
  selectedPriceSheet1: any;
  selectedPriceSheet2: any;
  priceSheetsItems1: any;
  priceSheetsItems2: any;
  currencyList: Array<any>;
  selectedCurrency: any;
  defaultPriceSheetNumbers: Array<any>;
  priceTypeForm: UntypedFormGroup;

  private _formGroupCache = new Map<string, UntypedFormGroup>();

  @ViewChild('priceSheetsDropdown1', {static: false})
  priceSheetsDropdown1: IconsDropdownComponent;

  @ViewChild('priceSheetsDropdown2', {static: false})
  priceSheetsDropdown2: IconsDropdownComponent;

  @ViewChild('scroll', {static: false})
  scroll: ElementRef | void;

  constructor(
    protected ref: ChangeDetectorRef,
    protected scorecardConnectorService: ScorecardConnectorService,
    protected brandService: BrandService,
    protected fb: UntypedFormBuilder,
    private priceSheetService: PriceSheetService,
    private pricingScorecardService: PricingScorecardService,
    @Inject(DOCUMENT) private document: any,
    private datePipe: DatePipe,
  ) {
    super(ref, scorecardConnectorService, brandService, fb);
  }

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get itemDetailsPrices(): any {
    return _.get(this.itemDetail, 'prices');
  }

  get currencyCode(): string {
    return _.get(this.selectedCurrency, 'code', 'EN');
  }

  get activePriceTypes(): Array<SegmentFieldItem> {
    return this.pricingScorecardService.activePriceTypes;
  }

  get dropdownPriceTypes(): Array<SegmentFieldItem> {
    return this.pricingScorecardService.dropdownPriceTypes;
  }

  get primaryPriceSheetNumber(): string {
    return this.pricingScorecardService.primaryPriceSheetNumber;
  }

  get selectedPriceSheet1Number(): string {
    return _.get(this.selectedPriceSheet1, 'price_sheet_number', '');
  }

  get selectedPriceSheet2Number(): string {
    return _.get(this.selectedPriceSheet2, 'price_sheet_number', '');
  }

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnInit() {
    super.ngOnInit();
    this.segmentForm = this.fb.group({});
    this.priceTypeForm = this.fb.group({
      priceType: new UntypedFormControl(''),
    });
    this.addCurrencyDropdownData();
    if (this.itemDetail) {
      this.handleAllDataUpdate();
    }
    this.runAnimation('show');
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onActiveElementBlur() {
    if (document.activeElement) {
      (<any>document.activeElement).blur();
    }
  }

  onCurrencyChange($event: any) {
    this.selectedCurrency = $event;
    this._formGroupCache.clear();
    this.ref.detectChanges();
  }

  onPriceSheetChange(priceSheet: any, number: number) {
    (<any>this)[`selectedPriceSheet${number}`] = priceSheet;
    this._formGroupCache.clear();
    if (number === 1) {
      this.pricingScorecardService.selectedPriceSheet = priceSheet;
      this.scorecardConnectorService.detectChangesInScorecard.emit();
    }
    this.ref.markForCheck();
  }

  onPriceTypeSortClick() {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    const order: Array<boolean | 'asc' | 'desc'> = this.sortDirection === 'asc' ? ['asc'] : ['desc'];
    this.segmentData = _.orderBy(this.segmentData, ['title'], order);
  }

  onPaste(event: any, selectedPriceSheet: any, item: any) {
    event.preventDefault();
    // get pasted data
    const clipboardData: any = event.clipboardData || (<any>window).clipboardData;
    if (!clipboardData) { return; }
    const pastedData: any = clipboardData.getData('Text');
    // split by newline
    const valuesArray = pastedData.split('\n');
    const index = _.findIndex(this.segmentData, item);
    // get segments sequence
    // set every price to the control
    _.each(valuesArray, (price: any, i: number) => {
      price = price.replace(',', '.');
      if (!price || isNaN(price)) { return; }
      const _item = this.segmentData[i];
      if (!_item) { return; }
      const control = this.getPriceControl(
        selectedPriceSheet.price_sheet_number.toString(),
        this.currencyCode,
          _item.item_name
      );
      const decimal = price.split('.')[1];
      if (decimal && decimal.length > 4) {
        price = price.slice(0, 7);
      }
      control.setValue(price);
      control.markAsDirty();
    });
  }

  onAddPriceTypeClick() {
    const selectedPriceType = this.priceTypeForm.get('priceType').value;
    if (!selectedPriceType) { return; }
    const priceType: any = _.remove(this.dropdownPriceTypes, ['item_name', selectedPriceType])[0];
    priceType.deletable = true;
    this.priceTypeForm.get('priceType').reset('');
    this.activePriceTypes.push(priceType);
    _.defer(() => {
      if (this.scroll) {
        this.scroll.nativeElement.scrollTop = this.scroll.nativeElement.scrollHeight;
      }
    });
    this.scorecardConnectorService.detectChangesInScorecard.emit();
  }

  onDeletePriceTypeClick(item: SegmentFieldItem) {
    const priceType: any = _.remove(this.activePriceTypes, ['item_name', item.item_name])[0];
    priceType.deletable = false;
    this.pricingScorecardService.addPriceTypeToDropdown(priceType);
    this.scorecardConnectorService.detectChangesInScorecard.emit();
  }

  onPriceStatusChange(status: any, item: SegmentFieldItem) {
    this.scorecardConnectorService.controlStatusChanged.emit({status, item, currencyCode: this.currencyCode});
  }

  onPricePopulatedChange(populated: any, item: SegmentFieldItem) {
    this.scorecardConnectorService.controlPopulatedChanged.emit({populated, item, currencyCode: this.currencyCode});
  }

  onPriceControlFocus(item: SegmentFieldItem) {
    this.scorecardConnectorService.controlFocused.emit({item, currencyCode: this.currencyCode});
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  public getSegmentFormData(): any {
    const formValue = _.get(this, 'segmentForm.value');
    return _.mapValues(formValue, (priceSheetData: any, priceSheetNumber: string) =>
      _.mapValues(priceSheetData, (priceTypeData: any, priceType: string) =>
        _.mapValues(priceTypeData, (currencyData: any, currencyCode: string) => {
          const result: any = { price: String(currencyData.price) };
          const uom = _.get(this.itemDetailsPrices, `${priceSheetNumber}.${priceType}.${currencyCode}.UOM`);
          if (uom) { result.UOM = uom; }
          if (currencyData.effective_date) { result.effective_date = currencyData.effective_date; }
          if (currencyData.expiration_date) { result.expiration_date = currencyData.expiration_date; }
          return result;
        })
      )
    );
  }

  /*******************************************
  * COMPONENT PROTECTED METHODS
  *******************************************/

  protected setBulkData(): void {
    this.priceSheetService.fetchAll(this.brandId)
    .subscribe((priceSheets: Array<any>) => {
      this.priceSheets = priceSheets;
      this.setData({'prices': this.getBulkSegmentData(this.segmentName)});
    });
  }

  protected setData(itemDetail: any): void {
    this.addDropdownPriceSheets(this.priceSheets);
    this.pricingScorecardService.findPrimaryPriceSheet(this.priceSheets);
    this.pricingScorecardService.separatePriceTypes(this.itemDetail, this.scoreData);
    this.buildForm();
  }

  protected updateControlsValidation(): void {
    this.pricingScorecardService.findPrimaryPriceSheet(this.priceSheets);
    this.pricingScorecardService.separatePriceTypes(this.itemDetail, this.scoreData);
    _.each(this.segmentForm.controls, (priceSheetForm: UntypedFormGroup, priceSheetNumber: string) => {
      _.each(priceSheetForm.controls, (priceTypeForm: UntypedFormGroup, priceType: string) => {
        _.each(priceTypeForm.controls, (currencyGroup: UntypedFormGroup, currencyCode: string) => {
          const validators = this.getControlValidators(priceSheetNumber, priceType, currencyCode);
          this.updateControlValidators(<UntypedFormControl>currencyGroup.get('price'), validators);
        });
      });
    });
  }

  /*
    * $event: {item: handleScoreDetailsExpand, currencyCode: string}
   */
  protected handleScoreDetailsExpand($event: any): void {
    if (!this.editMode) { return; }
    if (this.currencyCode !== $event.currencyCode) {
      const currency = _.find(this.currencyList, ['code', $event.currencyCode]);
      this.selectedCurrency = currency;
      this.ref.detectChanges();
    }
    _.defer(() => {
      const control = this.findControlByFieldMap($event.item.item_name);
      if (control) { control.focusControl($event.item.item_name); }
    });
  }

  protected findControlViaData(data: any): DetailsControlComponent {
    return this.findControlByFieldMap(data.item_name);
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  private buildForm(): void {
    _.each(this.priceSheets, (priceSheet: any) => {
      this.buildPriceSheetFormGroup(priceSheet, this.itemDetailsPrices);
    });
  }

  private buildPriceSheetFormGroup(priceSheet: any, prices: any) {
    const formGroup: UntypedFormGroup = new UntypedFormGroup({});

    _.each(this.segmentData, (data: any) => {

      const currencyFormGroup: UntypedFormGroup = new UntypedFormGroup({});
      _.each(this.currencyList, (currency: any) => {
        const basePath = `${priceSheet.price_sheet_number}.${data.item_name}.${currency.code}`;
        const price: string = _.get(prices, `${basePath}${this.bulkEdit ? '' : '.price'}`, '');
        const effectiveDate: string = _.get(prices, `${basePath}.effective_date`, '');
        const expirationDate: string = _.get(prices, `${basePath}.expiration_date`, '');
        const validators = this.getControlValidators(priceSheet.price_sheet_number, data.item_name, currency.code);
        currencyFormGroup.addControl(currency.code, new UntypedFormGroup({
          price: new UntypedFormControl(price, validators),
          effective_date: new UntypedFormControl(effectiveDate),
          expiration_date: new UntypedFormControl(expirationDate),
        }));
      });
      formGroup.addControl(data.item_name, currencyFormGroup);
    });
    if (this.segmentForm.get(priceSheet.price_sheet_number.toString())) {
      this.segmentForm.removeControl(priceSheet.price_sheet_number.toString());
    }
    this.segmentForm.addControl(priceSheet.price_sheet_number.toString(), formGroup);
  }

  private getPriceControl(priceSheetNumber: string, currency: string, priceType: string): UntypedFormControl {
    const priceSheetGroup = <UntypedFormGroup>this.segmentForm.controls[priceSheetNumber];
    const priceTypeGroup = <UntypedFormGroup>priceSheetGroup.controls[priceType];
    const currencyGroup = <UntypedFormGroup>priceTypeGroup.controls[currency];
    return <UntypedFormControl>currencyGroup.controls['price'];
  }

  public getPriceCurrencyFormGroup(priceSheetNumber: string, priceType: string, currencyCode: string): UntypedFormGroup {
    const key = `${priceSheetNumber}:${priceType}:${currencyCode}`;
    if (!this._formGroupCache.has(key)) {
      this._formGroupCache.set(key, <UntypedFormGroup>this.segmentForm.get(priceSheetNumber.toString()).get(priceType).get(currencyCode));
    }
    return this._formGroupCache.get(key);
  }

  /*
    * Check particular control validity
   */
  private isPriceControlValid(priceSheetNumber: string, currency: string, priceType: string): boolean {
    const currencyControl = this.getPriceControl(priceSheetNumber, currency, priceType);
    return _.get(currencyControl, 'valid', true);
  }

  /*
    * Checks table row validity
    * Every control of every currency in the price type must be valid
   */
  private isPriceTypeValid(priceSheetNumber: string, priceType: string): boolean {
    return _.every(this.currencyList, (currency: any) => {
      return this.isPriceControlValid(priceSheetNumber, currency.code, priceType);
    });
  }

  /*
    * Set currency data to the dropdown
    * Set active USD
   */
  private addCurrencyDropdownData(): void {
    this.currencyList = _.map(CurrencyCodeMapper.getCurrencyList(), (item: any) => {
      item.title = item.name;
      item.active = item.code === 'USD';
      item.selectedTitle = item.code;
      if (item.active) {
        this.selectedCurrency = item;
      }
      return item;
    });
  }

  /*
    * Set price sheets data to the 2 dropdowns
   */
  private addDropdownPriceSheets(priceSheets: any): void {
    const dropdownPriceSheets = _.cloneDeep(priceSheets);
    this.priceSheetsItems1 = dropdownPriceSheets;
    if (dropdownPriceSheets && dropdownPriceSheets.length > 1) {
      this.priceSheetsItems2 = _.cloneDeep(dropdownPriceSheets);
    }
    if (!this.priceSheetsItems1.length) { return; }
    const primary = _.find(this.priceSheetsItems1, ['default', 1]) || this.priceSheetsItems1[0];
    const secondary = _.find(this.priceSheetsItems1, ['default', 2]) || this.priceSheetsItems1[1];
    _.defer(() => {
      this.priceSheetsDropdown1.setActive(primary.price_sheet_number);
      if (!this.priceSheetsItems1[1]) { return; }
      this.priceSheetsDropdown2.setActive(secondary.price_sheet_number);
    });
  }

  private isDefaultPriceSheet(priceSheetNumber: string): boolean {
    return this.primaryPriceSheetNumber === priceSheetNumber;
  }

  private hasScore(priceSheetNumber: string, item: SegmentFieldItem): boolean {
    return _.has(this.segmentScores, `${priceSheetNumber}.${item.item_name}.${this.currencyCode}.price`);
  }

  private getScore(priceSheetNumber: string, priceType: string, currencyCode): any {
    return _.get(this.segmentScores, `${priceSheetNumber}.${priceType}.${currencyCode}.price`);
  }

  private hasPriceType(priceSheetNumber: string, item: SegmentFieldItem): boolean {
    return _.has(this.segmentScores, `${priceSheetNumber}.${item.item_name}`);
  }

  private isMasked(priceSheetNumber: string, item: SegmentFieldItem): boolean {
    if (!this.hasScores) { return false; }
    // mask out those default price sheets that are not in the scorecard
    if (this.isDefaultPriceSheet(priceSheetNumber)) {
      return !this.hasPriceType(priceSheetNumber, item);
    }
    return false;
  }

  private isMaskedControl(priceSheetNumber: string, item: SegmentFieldItem): boolean {
    if (!this.hasScores) { return false; }
    // mask out those controls that are not in the scorecard
    if (this.isDefaultPriceSheet(priceSheetNumber)) {
      return !this.hasScore(priceSheetNumber, item);
    }
    return false;
  }

  private getControlValidators(priceSheetNumber: string, priceType: string, currencyCode): Array<ValidatorFn> {
    const score = this.getScore(priceSheetNumber, priceType, currencyCode);
    const validators = [CustomValidators.number];
    const required = _.get(score, 'rule.required');
    if (required) { validators.push(Validators.required); }
    return validators;
  }

  /*
    * Returns value for the view mode with two decimals
   */
  private getPriceViewValue(priceSheetNumber: string, item: SegmentFieldItem): string | null {
    const currencyGroup = this.getPriceCurrencyFormGroup(priceSheetNumber.toString(), item.item_name, this.currencyCode);
    const value = _.get(currencyGroup, 'controls.price.value');
    if (!isNaN(value)) {
      const re = new RegExp('^-?\\d+(?:\.\\d{0,' + (2 || -1) + '})?');
      return Number(value).toFixed(2).toString().match(re)[0];
    }
    return null;
  }

  public getItemTitle (item: SegmentFieldItem): string {
    if (item.is_invalid === true) {
      return `${item.title} (Codes: ${this.itemDetail.inValidPriceTypes})`;
    } else {
      return item.title;
    }
  }

  public getFormattedPriceDate(priceSheetNumber: string, item: SegmentFieldItem, dateField: string): string {
    const currencyGroup = this.getPriceCurrencyFormGroup(priceSheetNumber, item.item_name, this.currencyCode);
    const value = _.get(currencyGroup, `controls.${dateField}.value`);
    return this.datePipe.transform(value, 'MM/dd/yy');
  }
}
